import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScanFace, Loader2, Check, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MyID WebSDK, iframe flow (https://docs.myid.uz/#/ru/websdk, FR-91).
 * The backend opens the session and builds `sdk_url`; this component only
 * shows the iframe as a full-screen overlay. iframe messages are UX signals —
 * on LIVENESS_PASSED we ask the backend, which reads the real outcome from MyID.
 * `sdk_url === null` means MOCK: no iframe, the backend completes the mock session.
 */

const MyIDStatus = {
  EXCEPTION: -1,
  IN_PROGRESS: 0,
  LIVENESS_PASSED: 1,
  LIVENESS_FAILED: 2,
  RETRY: 3,
  EXITED: 4,
} as const;

interface MyIdMessage {
  source?: string;
  status?: number;
  result_code?: number;
  result_note?: string;
}

interface MyIdWidgetProps {
  /** Opens a backend session; returns its iframe URL (null in MOCK) or null when it could not start. */
  onStart: () => Promise<{ sdkUrl: string | null } | null>;
  /** Liveness passed in the iframe (or MOCK) — the backend completes the session. */
  onPassed: () => Promise<void>;
  /** The cashier closed the overlay or the customer left MyID early. */
  onExit: () => void;
  /** Already verified (the cashier came back to this step) — go on without a new MyID session. */
  onContinue: () => void;
  verified: boolean;
  mock: boolean;
  disabled?: boolean;
}

export function MyIdWidget({ onStart, onPassed, onExit, onContinue, verified, mock, disabled }: MyIdWidgetProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [sdkUrl, setSdkUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const closeOverlay = useCallback(() => setSdkUrl(null), []);

  const finish = useCallback(async () => {
    closeOverlay();
    setBusy(true);
    try {
      await onPassed();
    } finally {
      setBusy(false);
    }
  }, [closeOverlay, onPassed]);

  const exit = useCallback(() => {
    closeOverlay();
    onExit();
  }, [closeOverlay, onExit]);

  const start = async () => {
    setBusy(true);
    try {
      const session = await onStart();
      if (!session) return;
      if (session.sdkUrl) {
        setSdkUrl(session.sdkUrl);
        return;
      }
      // MOCK: nothing to show — the backend passes the mock session right away
      await onPassed();
    } finally {
      setBusy(false);
    }
  };

  // The iframe needs the host screen size, on load and on every resize (MyID docs §7).
  const sendScreen = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { cmd: 'screen', screen: { width: window.screen.width, height: window.screen.height }, height: window.innerHeight, width: window.innerWidth },
      '*',
    );
  }, []);

  useEffect(() => {
    if (!sdkUrl) return;
    const origin = new URL(sdkUrl).origin;

    const onMessage = (e: MessageEvent<MyIdMessage>) => {
      if (e.origin !== origin || e.data?.source !== 'MyIDWebSDK') return;
      switch (e.data.status) {
        case MyIDStatus.LIVENESS_PASSED:
          void finish();
          break;
        case MyIDStatus.LIVENESS_FAILED:
          toast.error(t('murabaha_flow.myid.liveness_failed', { note: e.data.result_note ?? '' }));
          break;
        case MyIDStatus.EXITED:
          exit();
          break;
        case MyIDStatus.EXCEPTION:
          toast.error(t('murabaha_flow.myid.sdk_error'));
          exit();
          break;
      }
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('resize', sendScreen);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('resize', sendScreen);
    };
  }, [sdkUrl, finish, exit, sendScreen, t]);

  return (
    <div className="space-y-4">
      <div className="relative mx-auto flex min-h-[220px] w-full max-w-md items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed bg-muted p-6 text-center">
        {verified ? (
          <div className="space-y-2">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="h-6 w-6" />
            </div>
            <p className="text-base font-medium">{t('murabaha_flow.myid.done')}</p>
          </div>
        ) : (
          <div className="space-y-2 text-muted-foreground">
            <ScanFace className="mx-auto h-10 w-10" />
            <p className="text-base">{t('murabaha_flow.myid.widget_hint')}</p>
            {mock && (
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">
                {t('murabaha_flow.myid.mock_badge')}
              </Badge>
            )}
          </div>
        )}
      </div>

      {!verified && (
        <Button type="button" className="h-14 w-full text-base" disabled={disabled || busy} onClick={start}>
          {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ScanFace className="mr-2 h-5 w-5" />}
          {busy ? t('murabaha_flow.myid.verifying') : t('murabaha_flow.myid.start')}
        </Button>
      )}
      {verified && (
        <Button type="button" className="h-14 w-full text-base" disabled={disabled} onClick={onContinue}>
          {t('murabaha_flow.myid.continue')}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      )}

      {sdkUrl &&
        createPortal(
          <div className="fixed inset-0 z-[100] bg-background">
            <iframe
              ref={iframeRef}
              id="MyIDWebSDK"
              title="MyID"
              src={sdkUrl}
              allow="camera;fullscreen"
              allowFullScreen
              onLoad={sendScreen}
              className="h-full w-full border-0"
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label={t('murabaha_flow.myid.close')}
              className="absolute right-4 top-4 h-11 w-11 rounded-full shadow"
              onClick={exit}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>,
          document.body,
        )}
    </div>
  );
}
